
import {
  defineComponent, reactive, watch,
} from 'vue';
import Icone from '@/core/components/Icone.vue';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { useGradeBase } from '@/core/composables/GradeBase';
import { ECustomRenderRow } from '@/core/models/AntDesign/IColumn';
import ServicoMovimentoEstoque from '@/servicos/Estoques/ServicoMovimentoEstoque';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { IDTORastreabilidade } from '@/models/DTO/Estoques/Rastreabilidade/IDTORastreabilidade';
import { IAjusteInventario, IAjusteInventarioItem } from '@/models/Entidades/Estoques/IAjusteInventario';
import storeSistema from '@/store/storeSistema';

export default defineComponent({
  name: 'ResumoAjustesInventarioModal',
  props: {
    visivel: {
      type: Boolean,
    },
    empresa: {
      type: Number,
      required: true,
    },
    produtos: {
      type: Array as () => IDTORastreabilidade[],
      default: () => [],
    },
  },
  components: {
    Icone,
    RequisicaoModal,
  },
  emits: ['update:visivel', 'update:produtos', 'confirmacao'],
  setup(props, { emit }) {
    const { telaBase, apresentarMensagemSucesso } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso,
      ocultarBarraProgresso,
    } = useModalBase(props, emit);
    const { gradeBase } = useGradeBase();
    const servicoMovimentoEstoque = new ServicoMovimentoEstoque();
    servicoMovimentoEstoque.requisicaoSistema();
    telaBase.identificadorRecurso = 'AJUSTE_INVENTARIO';
    telaBase.identificadorPermissao = 'AUT_AJUSTE_INVENTARIO';

    const state = reactive({
      produtos: [] as IDTORastreabilidade[],
      observacao: '',
    });

    function preencherColunas() {
      gradeBase.colunas = [];
      gradeBase.colunas.push({
        title: 'Codigo', dataIndex: 'codigo', key: 'codigo', position: 0, visible: false,
      });
      gradeBase.colunas.push({
        title: 'Código Prod.', dataIndex: 'codigoProduto', key: 'CodigoProduto', position: 1, visible: true, ordering: true, align: 'left', width: 130,
      });
      gradeBase.colunas.push({
        title: 'Produto', dataIndex: 'descricaoProduto', key: 'DescricaoProduto', position: 2, visible: true, ordering: true, align: 'left', width: 280,
      });
      gradeBase.colunas.push({
        title: 'UN', dataIndex: 'unidade', key: 'Unidade', position: 3, visible: true, ordering: true, align: 'center', width: 60,
      });
      gradeBase.colunas.push({
        title: 'Estoque', dataIndex: 'descricaoEstoque', key: 'DescricaoEstoque', position: 4, visible: true, ordering: true, align: 'left', width: 240,
      });
      gradeBase.colunas.push({
        title: 'Identificador', dataIndex: 'identificador', key: 'Identificador', position: 5, visible: true, ordering: true, align: 'center', width: 120, customRenderRow: ECustomRenderRow.LinkAcao,
      });
      gradeBase.colunas.push({
        title: 'Lote', dataIndex: 'numeroLote', key: 'NumeroLote', position: 6, visible: true, ordering: true, align: 'center', width: 130,
      });
      gradeBase.colunas.push({
        title: 'Fabricação', dataIndex: 'dataFabricacao', key: 'DataFabricacao', position: 7, visible: true, ordering: true, align: 'center', width: 100, customRenderRow: ECustomRenderRow.MascararData,
      });
      gradeBase.colunas.push({
        title: 'Validade', dataIndex: 'dataValidade', key: 'DataValidade', position: 8, visible: true, ordering: true, align: 'center', width: 100, customRenderRow: ECustomRenderRow.MascararData,
      });
      gradeBase.colunas.push({
        title: 'Ajuste', dataIndex: 'qtdAjuste', key: 'QtdAjuste', position: 9, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado, width: 100,
      });
      gradeBase.colunas.push({
        title: 'Qtd Ajustada', dataIndex: 'qtdAjustada', key: 'QtdAjustada', position: 10, visible: true, ordering: true, align: 'right', customRenderRow: ECustomRenderRow.MascararFracionado, width: 100,
      });
    }

    function obterAjustePreenchido() {
      const ajuste = {} as IAjusteInventario;
      ajuste.codigoEmpresa = props.empresa;
      ajuste.codigoUsuario = storeSistema.getters.codigoUsuarioAutenticado();
      ajuste.observacao = state.observacao;
      ajuste.itens = [] as IAjusteInventarioItem[];
      state.produtos.forEach((item) => {
        if (item.qtdAjuste !== 0) {
          const itemAjuste = {} as IAjusteInventarioItem;
          itemAjuste.codigoProdutoDefinicao = item.codigoProdutoDefinicao;
          itemAjuste.codigoEstoqueItem = item.codigoItemEstoque;
          itemAjuste.codigoRastreabilidade = item.codigo;
          itemAjuste.qtdAjuste = item.qtdAjuste;
          ajuste.itens.push(itemAjuste);
        }
      });
      return ajuste;
    }

    function limparTela() {
      state.produtos = [];
      state.observacao = '';
    }

    async function salvar() {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };
      apresentarBarraProgresso();
      retorno = await servicoMovimentoEstoque.ajustarInventario(obterAjustePreenchido());
      ocultarBarraProgresso();

      if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
        apresentarMensagemSucesso('Ajustes realizados com sucesso!');
        emit('confirmacao');
        modalBase.computedVisivel = false;
      } else {
        apresentarRetornoRequisicao(retorno);
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      preencherColunas();
      limparTela();
      if (modalBase.computedVisivel) {
        props.produtos.forEach((item) => {
          if (item.qtdAjuste !== 0) {
            state.produtos.push(UtilitarioGeral.clonarObjeto(item));
          }
        });
      }
      telaBase.carregando = false;
    });

    return {
      props,
      state,
      telaBase,
      modalBase,
      gradeBase,
      ECustomRenderRow,
      UtilitarioGeral,
      UtilitarioData,
      UtilitarioMascara,
      salvar,
    };
  },
});
